import React from 'react';
import { 
  Stethoscope,
  SearchCheck,
  Activity,
  FileBarChart,
  CheckCircle2,
  AlertCircle,
  Calendar,
  BarChart2,
  Phone
} from 'lucide-react';
import { Helmet } from 'react-helmet-async';
import img from "../../Images/check.jpeg";

const EquipmentHealthPage = () => {
  const handleEmergencyCall = () => {
    window.location.href = 'tel:+9779802781400';
  };

  const services = [
    {
      title: "Diagnostic Assessment",
      description: "Comprehensive equipment health evaluation using advanced diagnostic tools and methodologies.",
      icon: <Stethoscope className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" />
    },
    {
      title: "Performance Analysis",
      description: "Detailed analysis of operational efficiency, wear patterns, and system performance metrics.",
      icon: <Activity className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" />
    },
    {
      title: "Predictive Diagnostics",
      description: "Advanced monitoring and early warning system for potential equipment issues.",
      icon: <SearchCheck className="w-6 h-6 sm:w-8 sm:h-8 text-emerald-600" />
    }
  ];

  const diagnosticFeatures = [
    {
      title: "Issue Detection Rate",
      description: "Early identification of potential problems",
      icon: <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />,
      stat: "98% Accuracy"
    },
    {
      title: "Prevention Rate",
      description: "Problems prevented through early detection",
      icon: <CheckCircle2 className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />,
      stat: "85% Success"
    },
    {
      title: "Response Time",
      description: "Quick diagnostic reporting and analysis",
      icon: <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" />,
      stat: "24-48 Hours"
    }
  ];

  const checkupProcess = [
    {
      title: "Initial Scan",
      description: "Preliminary equipment assessment and data collection"
    },
    {
      title: "Detailed Analysis",
      description: "In-depth diagnostic testing and performance evaluation"
    },
    {
      title: "Report Generation",
      description: "Comprehensive health status report with recommendations"
    },
    {
      title: "Action Planning",
      description: "Preventive maintenance and optimization strategy"
    }
  ];

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Equipment Health Checkup Services",
    "description": "Professional diagnostic services and equipment health monitoring for industrial machinery",
    "provider": {
      "@type": "Organization",
      "name": "Gortherm Energy",
      "url": "https://www.gorthermenergy.com"
    },
    "areaServed": {
      "@type": "Country",
      "name": "Nepal"
    }
  };

  return (
    <>
      <Helmet>
        <title>Equipment Health Checkup & Diagnostic Services | Predictive Maintenance | Gortherm Energy</title>
        <meta name="description" content="Professional equipment health checkup and diagnostic services for industrial machinery. Predictive maintenance, performance analysis, and condition monitoring to prevent breakdowns and optimize equipment lifespan." />
        <meta name="keywords" content="equipment health checkup, industrial diagnostics, predictive maintenance, condition monitoring, equipment performance analysis, machinery health assessment, preventive maintenance, diagnostic services Nepal" />
        
        {/* Open Graph */}
        <meta property="og:title" content="Equipment Health Checkup & Diagnostic Services | Gortherm Energy" />
        <meta property="og:description" content="Professional diagnostic services to ensure optimal performance and longevity of your industrial equipment. Advanced health monitoring and predictive maintenance." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.gorthermenergy.com/equipment-health" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Equipment Health Checkup Services | Gortherm Energy" />
        <meta name="twitter:description" content="Advanced equipment diagnostics and health monitoring for optimal industrial performance." />
        
        {/* Additional SEO */}
        <link rel="canonical" href="https://www.gorthermenergy.com/equipment-health" />
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
        <meta name="author" content="Gortherm Energy" />
        <meta name="language" content="English" />
        <meta name="geo.region" content="NP" />
        <meta name="geo.placename" content="Nepal" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <main className="w-full">
        {/* Hero Section */}
        <header className="relative h-64 sm:h-80 lg:h-96 bg-gradient-to-r from-emerald-900 to-teal-800 overflow-hidden pt-16 sm:pt-20">
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full flex items-center">
            <div className="text-white max-w-3xl">
              <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold mb-3 sm:mb-4">
                Equipment Health Checkup Services
              </h1>
              <p className="text-base sm:text-xl lg:text-2xl mb-6 sm:mb-8">
                Professional diagnostic services to ensure optimal performance and longevity of your industrial equipment.
              </p>
              <a href="/contact">
                <button className="bg-emerald-500 hover:bg-emerald-600 active:bg-emerald-700 text-white px-5 sm:px-6 py-2.5 sm:py-3 rounded-lg flex items-center gap-2 transition-all text-sm sm:text-base font-semibold w-full sm:w-auto">
                  Schedule Checkup
                  <Calendar className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                </button>
              </a>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          {/* Services Grid */}
          <section className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Diagnostic Services</h2>
            <p className="text-sm sm:text-base text-gray-600 max-w-3xl mx-auto px-4">
              Advanced equipment health monitoring and diagnostic solutions to prevent breakdowns 
              and optimize performance.
            </p>
          </section>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 mb-12 sm:mb-16">
            {services.map((service, index) => (
              <article 
                key={index}
                className="p-4 sm:p-6 rounded-xl border border-gray-200 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
              >
                <div className="mb-4" aria-hidden="true">
                  {service.icon}
                </div>
                <h3 className="text-lg sm:text-xl font-semibold mb-2 sm:mb-3">{service.title}</h3>
                <p className="text-sm sm:text-base text-gray-600">{service.description}</p>
              </article>
            ))}
          </div>

          {/* Diagnostic Features */}
          <section className="bg-emerald-50 rounded-xl p-6 sm:p-8 mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-6 sm:mb-8 text-center">Diagnostic Excellence</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
              {diagnosticFeatures.map((feature, index) => (
                <div key={index} className="text-center p-4 sm:p-6 bg-white rounded-lg shadow-sm">
                  <div className="flex justify-center mb-3 sm:mb-4" aria-hidden="true">
                    {feature.icon}
                  </div>
                  <h3 className="text-lg sm:text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-xs sm:text-sm text-gray-600 mb-3 sm:mb-4">{feature.description}</p>
                  <div className="text-xl sm:text-2xl font-bold text-emerald-600">{feature.stat}</div>
                </div>
              ))}
            </div>
          </section>

          {/* Checkup Process */}
          <section className="mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl font-bold mb-8 sm:mb-12 text-center">Diagnostic Process</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-6 sm:gap-8">
              {checkupProcess.map((step, index) => (
                <div key={index} className="relative">
                  <div className="flex flex-col items-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-emerald-100 rounded-full flex items-center justify-center mb-3 sm:mb-4">
                      <span className="text-emerald-600 font-bold text-sm sm:text-base">{index + 1}</span>
                    </div>
                    <h3 className="text-base sm:text-lg font-semibold mb-2 text-center">{step.title}</h3>
                    <p className="text-xs sm:text-sm text-gray-600 text-center">{step.description}</p>
                  </div>
                  {index < checkupProcess.length - 1 && (
                    <div className="hidden md:block absolute top-6 left-[60%] w-[80%] h-[2px] bg-emerald-100"></div>
                  )}
                </div>
              ))}
            </div>
          </section>

          {/* Advanced Tools Section */}
          <section className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center mb-12 sm:mb-16">
            <div className="relative h-64 sm:h-80 md:h-96 order-2 md:order-1">
              <img 
                src={img} 
                alt="Advanced equipment diagnostic tools and monitoring systems"
                className="rounded-lg shadow-xl object-cover h-full w-full"
                loading="lazy"
                width="600"
                height="400"
              />
            </div>
            <div className="order-1 md:order-2">
              <h2 className="text-2xl sm:text-3xl font-bold mb-4 sm:mb-6">Advanced Diagnostic Tools</h2>
              <div className="space-y-4 sm:space-y-6">
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2 bg-emerald-100 rounded-lg flex-shrink-0">
                    <Activity className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 sm:mb-2 text-base sm:text-lg">State-of-the-Art Equipment</h3>
                    <p className="text-sm sm:text-base text-gray-600">
                      Latest diagnostic tools and monitoring equipment for accurate health assessment.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3 sm:gap-4">
                  <div className="p-2 bg-emerald-100 rounded-lg flex-shrink-0">
                    <FileBarChart className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" aria-hidden="true" />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1 sm:mb-2 text-base sm:text-lg">Detailed Reporting</h3>
                    <p className="text-sm sm:text-base text-gray-600">
                      Comprehensive health reports with actionable insights and recommendations.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* Health Monitoring CTA */}
          <aside className="bg-emerald-50 rounded-xl p-6 sm:p-8">
            <div className="text-center">
              <h2 className="text-2xl sm:text-3xl font-bold mb-3 sm:mb-4">Continuous Health Monitoring</h2>
              <p className="text-sm sm:text-base text-gray-600 mb-6">
                Subscribe to our continuous monitoring service for real-time equipment health updates.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={handleEmergencyCall}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg flex items-center gap-2 justify-center transition-all text-sm sm:text-base font-semibold"
                >
                  Start Monitoring
                  <BarChart2 className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                </button>
                <a href="tel:+9779802781400">
                  <button className="bg-white border-2 border-emerald-600 text-emerald-600 hover:bg-emerald-50 px-6 sm:px-8 py-2.5 sm:py-3 rounded-lg flex items-center gap-2 justify-center transition-all text-sm sm:text-base font-semibold w-full sm:w-auto">
                    <Phone className="w-4 h-4 sm:w-5 sm:h-5" aria-hidden="true" />
                    Call Now
                  </button>
                </a>
              </div>
              <p className="mt-4 text-sm sm:text-base text-gray-600">
                Call: <a href="tel:+9779802781400" className="text-emerald-600 hover:text-emerald-700 font-semibold">+977 980 278 1400</a>
              </p>
            </div>
          </aside>
        </div>
      </main>
    </>
  );
};

export default EquipmentHealthPage;